import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CreditCard, Banknote, Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { shippingAreasQuery, type ShippingArea } from "@/lib/api/admin";
import { createOrder, initiatePayment } from "@/lib/api/orders";
import { cartQuery, currentUserQuery, myOrdersQuery, profileQuery } from "@/lib/api/queries";
import { queryCache } from "@/lib/api/queryCache";
import type { Cart, ShippingAddress } from "@/lib/api/types";
import { Icon } from "@/components/store/Icon";
import { Reveal } from "@/components/motion/Reveal";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { imgUrl } from "@/lib/format";
import { AuthModal } from "@/components/store/AuthModal";

const EMPTY_CART: Cart = {
  items: [],
  subtotal: 0,
  shipping: 0,
  tax: 0,
  discount: 0,
  total: 0,
  promoCode: null,
};

const FIELDS_BEFORE: { key: keyof ShippingAddress; span: string; type?: string }[] = [
  { key: "firstName", span: "md:col-span-1" },
  { key: "lastName", span: "md:col-span-1" },
  { key: "phone", span: "md:col-span-2", type: "tel" },
];

const inputClass =
  "w-full bg-surface-container-low border border-outline-variant/40 px-4 py-3.5 rounded-xl font-body-md text-primary placeholder:text-on-surface-variant/40 outline-none transition-all focus:border-secondary focus:bg-surface-container-lowest focus:ring-2 focus:ring-secondary/20";

function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useFormatters();
  const { productName, colorName, sizeName } = useLocalizedContent();
  const { data: cartData } = useSuspenseQuery(cartQuery());
  const cart: Cart = cartData ?? EMPTY_CART;
  const { data: areas } = useQuery(shippingAreasQuery());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: user } = useQuery(currentUserQuery());
  const { data: savedProfile } = useQuery({ ...profileQuery(), enabled: !!user });
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (user === null) {
      setShowAuthModal(true);
    } else {
      setShowAuthModal(false);
    }
  }, [user]);

  const [address, setAddress] = useState<ShippingAddress>({
    firstName: "",
    lastName: "",
    address: "",
    city: "",
    postcode: "",
    phone: "",
  });
  const [areaName, setAreaName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<'fawaterak' | 'cod'>('fawaterak');

  const areaLabel = (area: ShippingArea) => area.name;
  const selectedArea = useMemo(
    () => (areas ?? []).find((area) => area.name === areaName),
    [areas, areaName],
  );
  const shippingFee = selectedArea?.price ?? cart.shipping;
  const total = cart.total - cart.shipping + shippingFee;

  const orderMutation = useMutation({
    mutationFn: () => createOrder({ 
      address: { 
        ...address, 
        city: areaName || "Default City", 
        governorate: areaName || "Default Governorate",
        postcode: "" 
      }, 
      paymentMethod,
      shippingAreaId: selectedArea?.id 
    }),
    onSuccess: async (order) => {
      const orderId = (order as any).id || (order as any).orderId;
      const orderNum = (order as any).orderNumber || (order as any).order_number || orderId || order.id;

      if (paymentMethod === "fawaterak") {
        const directPaymentUrl = (order as any).paymentUrl || (order as any).payment_url;
        if (directPaymentUrl) {
          window.location.href = directPaymentUrl;
          return;
        }

        toast.info(t("checkout.preparingPayment", { defaultValue: "Preparing secure payment gateway..." }));
        try {
          const payRes = await initiatePayment(orderId);
          if (payRes.success && payRes.paymentUrl) {
            window.location.href = payRes.paymentUrl;
            return;
          } else {
            throw new Error(payRes.message || "Could not initiate online payment");
          }
        } catch (err: any) {
          toast.error(err.message || "Payment initiation failed");
          return;
        }
      }

      queryCache.invalidate(cartQuery().queryKey);
      queryCache.invalidate(myOrdersQuery().queryKey);
      queryClient.invalidateQueries({ queryKey: cartQuery().queryKey });
      queryClient.invalidateQueries({ queryKey: myOrdersQuery().queryKey });
      navigate(`/order-success?n=${orderNum}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });


  const applySavedAddress = () => {
    if (!savedProfile) return;
    const parts = (savedProfile.fullName ?? "").trim().split(/\s+/);
    setAddress((prev) => ({
      ...prev,
      firstName: parts[0] ?? prev.firstName,
      lastName: parts.slice(1).join(" ") || prev.lastName,
      address: savedProfile.street || prev.address,
      city: savedProfile.city || prev.city,
      phone: savedProfile.phone || prev.phone,
    }));
    if (savedProfile.governorate) setAreaName(savedProfile.governorate);
    toast.success(t("checkout.savedAddressApplied"));
  };

  const hasSavedAddress = Boolean(
    savedProfile && (savedProfile.street || savedProfile.city || savedProfile.phone),
  );

  const isAddressComplete = Boolean(
    address.firstName?.trim() &&
    address.lastName?.trim() &&
    address.phone?.trim() &&
    address.address?.trim() &&
    (areas && areas.length > 0 ? areaName?.trim() : true)
  );

  const handleCompletePurchase = () => {
    if (!address.firstName?.trim()) {
      toast.error(t("checkout.errors.firstName", { defaultValue: "Please enter your first name" }));
      document.getElementById("firstName")?.focus();
      return;
    }
    if (!address.lastName?.trim()) {
      toast.error(t("checkout.errors.lastName", { defaultValue: "Please enter your last name" }));
      document.getElementById("lastName")?.focus();
      return;
    }
    if (!address.phone?.trim()) {
      toast.error(t("checkout.errors.phone", { defaultValue: "Please enter your phone number" }));
      document.getElementById("phone")?.focus();
      return;
    }
    if (areas && areas.length > 0 && !areaName?.trim()) {
      toast.error(t("checkout.errors.area", { defaultValue: "Please select a shipping area" }));
      document.getElementById("shippingArea")?.focus();
      return;
    }
    if (!address.address?.trim()) {
      toast.error(t("checkout.errors.address", { defaultValue: "Please enter your address" }));
      document.getElementById("address")?.focus();
      return;
    }
    if (!cart.items || cart.items.length === 0) {
      toast.error(t("cart.empty", { defaultValue: "Your bag is empty" }));
      return;
    }

    orderMutation.mutate();
  };


  return (
    <StoreLayout>
      <main className="pt-10 pb-section-gap-desktop px-grid-margin max-w-7xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 cursor-pointer hover:bg-surface-container-low transition-colors">
          <ArrowLeft className="w-4 h-4 me-2" />{t('product.back', { defaultValue: 'Back' })}
        </Button>
        <h1 className="sr-only">{t("checkout.title")}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter">
          {/* Left */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            {/* Step 1: Payment Method */}
            <Reveal as="section" className="bg-surface-container-lowest p-8 rounded-2xl luxury-shadow border border-outline-variant/20">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-headline-md text-headline-md">{t("checkout.payment")}</h2>
                <span className="text-label-sm font-label-sm bg-secondary-container/20 text-secondary px-3 py-1 rounded-full uppercase">
                  {t("checkout.step", { current: 1, total: 2 })}
                </span>
              </div>
              <div className="space-y-4">
                {/* 1. Online Payment (Fawaterak) */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setPaymentMethod('fawaterak')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPaymentMethod('fawaterak'); } }}
                  className={`border-2 p-5 sm:p-6 rounded-2xl flex items-center justify-between gap-4 cursor-pointer transition-all duration-200 ${
                    paymentMethod === 'fawaterak'
                      ? 'border-primary bg-primary/[0.04] ring-1 ring-primary/20 shadow-sm'
                      : 'border-outline-variant/30 bg-surface-container-lowest hover:border-primary/40 hover:bg-surface-container-low/40'
                  }`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                      paymentMethod === 'fawaterak'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-primary/10 text-primary border border-primary/20'
                    }`}>
                      <CreditCard className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-label-md text-label-md font-bold uppercase tracking-wider text-primary truncate">
                        {t("checkout.onlinePayment", { defaultValue: "Online Payment (Fawaterak)" })}
                      </span>
                      <span className="text-xs text-on-surface-variant/70 mt-0.5">
                        {t("checkout.onlinePaymentHint", { defaultValue: "Pay securely using Credit Cards, Wallets or Fawry" })}
                      </span>
                    </div>
                  </div>
                  {paymentMethod === 'fawaterak' && (
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </span>
                  )}
                </div>

                {/* 2. Cash On Delivery (COD) */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setPaymentMethod('cod')}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPaymentMethod('cod'); } }}
                  className={`border-2 p-5 sm:p-6 rounded-2xl flex items-center justify-between gap-4 cursor-pointer transition-all duration-200 ${
                    paymentMethod === 'cod'
                      ? 'border-primary bg-primary/[0.04] ring-1 ring-primary/20 shadow-sm'
                      : 'border-outline-variant/30 bg-surface-container-lowest hover:border-primary/40 hover:bg-surface-container-low/40'
                  }`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                      paymentMethod === 'cod'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-secondary/10 text-secondary border border-secondary/20'
                    }`}>
                      <Banknote className="w-6 h-6" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-label-md text-label-md font-bold uppercase tracking-wider text-primary truncate">
                        {t("checkout.cod", { defaultValue: "Cash On Delivery (COD)" })}
                      </span>
                      <span className="text-xs text-on-surface-variant/70 mt-0.5">
                        {t("checkout.codHint", { defaultValue: "Pay in cash when you receive your order" })}
                      </span>
                    </div>
                  </div>
                  {paymentMethod === 'cod' && (
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </span>
                  )}
                </div>
              </div>
            </Reveal>

            {/* Step 2: Shipping Address */}
            <Reveal as="section" delay={0.08} className="bg-surface-container-lowest p-6 sm:p-8 rounded-2xl luxury-shadow border border-outline-variant/20">
              <div className="flex items-center justify-between gap-4 mb-6">
                <h2 className="font-headline-md text-headline-md">{t("checkout.shippingAddress")}</h2>
                <span className="shrink-0 text-label-sm font-label-sm bg-secondary-container/20 text-secondary px-3 py-1 rounded-full uppercase">
                  {t("checkout.step", { current: 2, total: 2 })}
                </span>
              </div>

              {hasSavedAddress && (
                <button
                  type="button"
                  onClick={applySavedAddress}
                  className="mb-6 inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/5 px-5 py-2.5 font-label-sm text-label-sm uppercase tracking-[0.14em] text-secondary transition-all hover:bg-secondary/10 active:scale-[0.98]"
                >
                  <Icon name="bookmark" className="text-[18px]" />
                  {t("checkout.useSavedAddress")}
                </button>
              )}

              <form className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {FIELDS_BEFORE.map((field) => (
                  <div key={field.key} className={`${field.span} flex flex-col gap-2`}>
                    <label
                      htmlFor={field.key}
                      className="font-label-sm text-label-sm uppercase tracking-[0.14em] text-on-surface-variant"
                    >
                      {t(`checkout.fields.${field.key}`)}
                    </label>
                    <input
                      id={field.key}
                      type={field.type ?? "text"}
                      placeholder={t(`checkout.placeholders.${field.key}`)}
                      value={address[field.key]}
                      onChange={(event) =>
                        setAddress((prev) => ({ ...prev, [field.key]: event.target.value }))
                      }
                      className={inputClass}
                    />
                  </div>
                ))}
                <div className="md:col-span-2 flex flex-col gap-2">
                  <label
                    htmlFor="shippingArea"
                    className="font-label-sm text-label-sm uppercase tracking-[0.14em] text-on-surface-variant"
                  >
                    {t("checkout.shippingArea")}
                  </label>
                  <select
                    id="shippingArea"
                    value={areaName}
                    onChange={(event) => setAreaName(event.target.value)}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="">—</option>
                    {(areas ?? []).map((area) => (
                      <option key={area.id} value={area.name}>
                        {areaLabel(area)} — {formatPrice(area.price)}
                      </option>
                    ))}
                  </select>
                  {selectedArea && (
                    <p className="mt-2 font-label-sm text-label-sm text-on-surface-variant">
                      {t("checkout.deliveryEstimate", {
                        days: String(selectedArea.days),
                      })}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2 flex flex-col gap-2">
                  <label
                    htmlFor="address"
                    className="font-label-sm text-label-sm uppercase tracking-[0.14em] text-on-surface-variant"
                  >
                    {t("checkout.fields.address")}
                  </label>
                  <input
                    id="address"
                    type="text"
                    placeholder={t("checkout.placeholders.address")}
                    value={address.address}
                    onChange={(event) =>
                      setAddress((prev) => ({ ...prev, address: event.target.value }))
                    }
                    className={inputClass}
                  />
                </div>
              </form>
            </Reveal>
          </div>

          {/* Right */}
          <div className="lg:col-span-5">
            <div className="sticky top-32 space-y-6">
              <Reveal delay={0.16} className="bg-surface-container-lowest p-8 rounded-lg luxury-shadow">
                <h3 className="font-headline-md text-headline-md mb-8 pb-4 border-b border-outline-variant/30">
                  {t("cart.summary")}
                </h3>

                <div className="space-y-6 mb-8">
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex gap-4">
                      <div className="w-20 h-24 bg-surface-container-low rounded-lg overflow-hidden shrink-0">
                        <img
                          src={imgUrl(item.image)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-grow">
                        <p className="font-label-md text-label-md uppercase mb-1">
                          {productName(item.name)}
                        </p>
                        <p className="text-label-sm text-on-surface-variant mb-2">
                          {t("checkout.itemLine", {
                            size: sizeName(item.size),
                            color: colorName(item.color),
                          })}
                        </p>
                        <p className="font-body-md text-primary">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-6 border-t border-outline-variant/30 text-on-surface-variant font-label-md text-label-md">
                  <div className="flex justify-between">
                    <span>{t("common.subtotal")}</span>
                    <span>{formatPrice(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("checkout.expressShipping", { defaultValue: "Standard Shipping" })}</span>
                    <span>
                      {shippingFee === 0
                        ? t("common.complimentary")
                        : formatPrice(shippingFee)}
                    </span>
                  </div>
                  {cart.discount > 0 && (
                    <div className="flex justify-between text-secondary">
                      <span>{t("cart.discount", { code: cart.promoCode })}</span>
                      <span>−{formatPrice(cart.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>{t("checkout.taxes")}</span>
                    <span>{formatPrice(cart.tax)}</span>
                  </div>
                  <div className="flex justify-between pt-4 text-primary font-bold text-lg">
                    <span>{t("common.total")}</span>
                    <span>{formatPrice(total)}</span>
                  </div>
                </div>




                <button
                  type="button"
                  disabled={orderMutation.isPending}
                  onClick={handleCompletePurchase}
                  className={`w-full mt-8 py-5 rounded-lg font-label-md text-label-md uppercase tracking-widest flex items-center justify-center gap-3 transition-all duration-200 cursor-pointer ${
                    !isAddressComplete
                      ? "bg-primary/50 text-on-primary/70 opacity-60 hover:opacity-75 shadow-none"
                      : "bg-primary text-on-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <Icon name="verified_user" />
                  {orderMutation.isPending ? t("checkout.placing") : t("checkout.complete")}
                </button>

                <p className="text-center mt-6 text-label-sm text-on-surface-variant/60">
                  {t("checkout.legal")}{" "}
                  <a className="underline" href="#">
                    {t("checkout.terms")}
                  </a>{" "}
                  {t("checkout.and")}{" "}
                  <a className="underline" href="#">
                    {t("checkout.privacy")}
                  </a>
                  .
                </p>
              </Reveal>

              <div className="flex justify-center gap-8 text-on-surface-variant/50">
                {[
                  { icon: "local_shipping", label: t("checkout.badges.carbonNeutral") },
                  { icon: "published_with_changes", label: t("checkout.badges.returns") },
                  { icon: "encrypted", label: t("checkout.badges.securePayment") },
                ].map((badge) => (
                  <div key={badge.icon} className="flex flex-1 flex-col items-center justify-start gap-1 text-center">
                    <Icon name={badge.icon} className="text-2xl" />
                    <span className="block w-full text-center text-[10px] uppercase font-bold tracking-tighter">
                      {badge.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      <AuthModal
        open={showAuthModal}
        onClose={() => {
          // If user closes without logging in and still not authenticated, go back
          if (!user) navigate(-1);
          setShowAuthModal(false);
        }}
        onSuccess={() => setShowAuthModal(false)}
      />
    </StoreLayout>
  );
}

export default CheckoutPage;
